import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Logo } from '../components/ui/Logo.js';
import { Icon } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';

const PASSWORD_RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: 'At least 12 characters', test: (p) => p.length >= 12 },
];

const inputStyle: CSSProperties = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: '12px 14px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
};

const labelStyle: CSSProperties = {
  display: 'block',
  marginBottom: 6,
  color: 'var(--ink-2)',
  fontSize: 13,
  fontWeight: 500,
};

const errorStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--danger)',
  background: 'color-mix(in oklab, var(--danger) 14%, transparent)',
  border: '1px solid color-mix(in oklab, var(--danger) 32%, transparent)',
  borderRadius: 12,
  padding: '10px 14px',
};

const TRIAL_BULLETS = [
  'Personalized weekly plans for your family size, budget, and ZIP',
  'Shopping lists grouped by store aisle, trimmed to what you actually need',
  'Dietary needs and the avoid list honored on every plate',
  '30 days free, then $9.99 / month — cancel any time',
];

export function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < 12) { setError('Password must be at least 12 characters.'); return; }
    if (!inviteCode.trim()) { setError('An invite code is required.'); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/signup', { name, email, password, inviteCode: inviteCode.trim() });
      await api.post('/api/auth/login', { email, password });
      await qc.invalidateQueries({ queryKey: ['profile'] });
      navigate('/onboarding', { replace: true });
    } catch (err: any) {
      setError(err.body?.error ?? 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background: 'var(--bg-deep)',
        backgroundImage: 'radial-gradient(60% 40% at 50% 0%, var(--accent-soft), transparent 70%)',
      }}
    >
      <div
        className="w-full max-w-md p-8 sm:p-10"
        style={{
          background: 'var(--surface)',
          borderRadius: 22,
          border: '1px solid var(--line-soft)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {/* ── Brand mark + back affordance ─────────────────────────── */}
        <div className="flex items-center justify-between mb-8">
          <Logo />
          <Link
            to="/"
            className="text-xs font-mono hover:underline inline-flex items-center gap-1.5"
            style={{ color: 'var(--ink-4)' }}
          >
            <Icon name="chev" size={11} style={{ transform: 'rotate(180deg)' }} />
            home
          </Link>
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            marginBottom: 10,
          }}
        >
          30 days, on us
        </div>
        <h1 className="h-display" style={{ fontSize: 36, lineHeight: 1.05, margin: 0, color: 'var(--ink)' }}>
          Start your{' '}
          <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>trial.</span>
        </h1>
        <p className="text-sm mt-3 mb-6" style={{ color: 'var(--ink-3)' }}>
          We'll have your first weekly plan ready in about a minute.
        </p>

        {/* Trial bullets — small, calm, framed with ink-3/4 */}
        <ul style={{ marginBottom: 24, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {TRIAL_BULLETS.map((b) => (
            <li
              key={b}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                fontSize: 12.5,
                color: 'var(--ink-3)',
                lineHeight: 1.5,
              }}
            >
              <span
                style={{
                  marginTop: 6,
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  background: 'var(--accent)',
                  flexShrink: 0,
                }}
              />
              {b}
            </li>
          ))}
        </ul>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="signup-name" style={labelStyle}>Full name</label>
            <input
              id="signup-name"
              type="text"
              autoComplete="name"
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label htmlFor="signup-email" style={labelStyle}>Email</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label htmlFor="signup-invite" style={labelStyle}>Invite code</label>
            <input
              id="signup-invite"
              type="text"
              autoComplete="off"
              required
              placeholder="XXXX-XXXX-XXXX"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}
            />
          </div>
          <div>
            <label htmlFor="signup-password" style={labelStyle}>Password</label>
            <input
              id="signup-password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
            />
            <ul style={{ marginTop: 8, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {PASSWORD_RULES.map(({ label, test }) => {
                const ok = test(password);
                return (
                  <li
                    key={label}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: ok ? 'var(--accent)' : 'var(--ink-4)',
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: ok ? 'var(--accent-soft)' : 'transparent',
                        border: ok ? '1px solid transparent' : '1px solid var(--line-soft)',
                        flexShrink: 0,
                      }}
                    >
                      {ok && <Icon name="check" size={10} stroke={2.6} />}
                    </span>
                    {label}
                  </li>
                );
              })}
            </ul>
          </div>

          {error && <p style={errorStyle}>{error}</p>}

          <Button
            type="submit"
            disabled={loading}
            className="w-full"
            style={{ marginTop: 4 }}
          >
            {loading ? 'Creating account…' : 'Start 30-day trial'}
            {!loading && <Icon name="arrow" size={14} />}
          </Button>

          <p
            style={{
              textAlign: 'center',
              fontSize: 11.5,
              color: 'var(--ink-4)',
              lineHeight: 1.5,
            }}
          >
            By creating an account you agree to our terms.<br />
            $9.99 / month after your 30-day trial. Cancel any time.
          </p>
        </form>

        <p className="text-center mt-6" style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Already have an account?{' '}
          <Link
            to="/login"
            className="hover:underline"
            style={{ color: 'var(--accent)', fontWeight: 500 }}
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
